/**
 * 🎨 RINTINTIN DESIGN SYSTEM - COLOR TOKENS
 * =========================================
 * 
 * Système de couleurs cohérent basé sur la charte existante
 * Inspiré du thème professionnel pet-care avec touches modernes
 */

export const colors = {
  // ==========================================
  // COULEURS PRINCIPALES - RINTINTIN BRANDING
  // ==========================================
  primary: {
    50: '#f6f3f0',   // Marron très clair
    100: '#e9e1d8',  // Marron clair 2
    200: '#d4c4a8',  // Marron clair
    300: '#c4a882',  // Marron medium-clair
    400: '#A5765C',  // MARRON PRINCIPAL RINTINTIN 🎨
    500: '#926b57',  // Marron medium
    600: '#7d5a4a',  // Marron foncé
    700: '#68493d',  // Marron très foncé
    800: '#533830',  // Marron dark
    900: '#3e2723',  // Marron noir
  },

  // ==========================================
  // COULEURS SECONDAIRES - VERT RINTINTIN
  // ==========================================
  secondary: {
    50: '#f0f8f4',   // Vert très clair
    100: '#dcf4e6',  // Vert clair 2
    200: '#b8e6cc',  // Vert clair
    300: '#8dd4a8',  // Vert medium-clair
    400: '#7FB069',  // VERT PRINCIPAL RINTINTIN 🌿
    500: '#6ea356',  // Vert medium
    600: '#5d8749',  // Vert foncé
    700: '#4c6b3c',  // Vert très foncé
    800: '#3b4f2f',  // Vert dark
    900: '#2a3422',  // Vert noir
  },

  // ==========================================
  // COULEURS FONCTIONNELLES - ÉTATS
  // ==========================================
  success: {
    50: '#f0f8f4',   // Vert Rintintin très clair
    100: '#dcf4e6',  // Vert Rintintin clair 2
    200: '#b8e6cc',  // Vert Rintintin clair
    300: '#8dd4a8',  // Vert Rintintin medium-clair
    400: '#7FB069',  // VERT PRINCIPAL RINTINTIN 🌿
    500: '#6ea356',  // Vert Rintintin medium (PRINCIPAL SUCCESS)
    600: '#5d8749',  // Vert Rintintin foncé
    700: '#4c6b3c',  // Vert Rintintin très foncé
    800: '#3b4f2f',  // Vert Rintintin dark
    900: '#2a3422',  // Vert Rintintin noir
  },

  warning: {
    50: '#fffbe6',   // Ant Design warning-1
    100: '#fff1b8',  // Ant Design warning-2
    200: '#ffe58f',  // Ant Design warning-3
    300: '#ffd666',  // Ant Design warning-4
    400: '#ffc53d',  // Ant Design warning-5
    500: '#faad14',  // Ant Design warning-6 (PRINCIPAL)
    600: '#d48806',  // Ant Design warning-7
    700: '#ad6800',  // Ant Design warning-8
    800: '#874d00',  // Ant Design warning-9
    900: '#613400',  // Ant Design warning-10
  },

  error: {
    50: '#fff2f0',   // Ant Design error-1
    100: '#ffccc7',  // Ant Design error-2
    200: '#ffa39e',  // Ant Design error-3
    300: '#ff7875',  // Ant Design error-4
    400: '#ff4d4f',  // Ant Design error-5 (PRINCIPAL)
    500: '#f5222d',  // Ant Design error-6
    600: '#cf1322',  // Ant Design error-7
    700: '#a8071a',  // Ant Design error-8
    800: '#820014',  // Ant Design error-9
    900: '#5c0011',  // Ant Design error-10
  },

  info: {
    50: '#ebf8ff',
    100: '#bee3f8',
    200: '#90cdf4',
    300: '#63b3ed',
    400: '#4299e1',
    500: '#3182ce',
    600: '#2b77cb',
    700: '#2c5aa0',
    800: '#2a4365',
    900: '#1a365d',
  },

  // ==========================================
  // COULEURS NEUTRES - INTERFACE
  // ==========================================
  neutral: {
    0: '#ffffff',    // Blanc pur
    50: '#fafafa',   // Quasi-blanc
    100: '#f5f5f5',  // Gris très clair
    200: '#e5e5e5',  // Gris clair
    300: '#d4d4d4',  // Gris medium-clair  
    400: '#a3a3a3',  // Gris medium
    500: '#737373',  // Gris
    600: '#525252',  // Gris dark
    700: '#404040',  // Gris très dark
    800: '#262626',  // Quasi-noir
    900: '#171717',  // Noir intense
    1000: '#000000', // Noir pur
  },

  // ==========================================
  // COULEURS SPÉCIALISÉES - MÉTIER
  // ==========================================
  pet: {
    // Couleurs spécifiques au métier animalier
    dog: '#8B4513',      // Marron chien
    cat: '#FF6B35',      // Orange chat
    bird: '#4ECDC4',     // Turquoise oiseau
    rabbit: '#FFE66D',   // Jaune lapin
    other: '#95A5A6',    // Gris autres
  },

  booking: {
    // États de réservation
    pending: '#F6C674',     // En attente
    confirmed: '#7FB069',   // Confirmé
    cancelled: '#e53e3e',   // Annulé
    completed: '#3182ce',   // Terminé
  },

  // ==========================================
  // ALIASES SÉMANTIQUES
  // ==========================================
  background: {
    primary: '#ffffff',
    secondary: '#fafafa', 
    tertiary: '#f5f5f5',
  },

  text: {
    primary: '#171717',
    secondary: '#525252',
    tertiary: '#737373',
    inverse: '#ffffff',
  },

  border: {
    light: '#e5e5e5',
    medium: '#d4d4d4',
    dark: '#a3a3a3',
  },
} as const;

// ==========================================
// TYPES POUR TYPESCRIPT
// ==========================================
export type ColorToken = keyof typeof colors;
export type ColorVariant<T extends ColorToken> = keyof typeof colors[T];

// ==========================================
// HELPERS POUR UTILISATION
// ==========================================
export const getColor = <T extends ColorToken>(
  token: T,
  variant: ColorVariant<T>
) => {
  return colors[token][variant] as string;
};

// Exemples d'usage :
// getColor('primary', 400) → '#7FB069'
// getColor('neutral', 200) → '#e5e5e5'
